import { CreateOrUpdatePronounTranslationInput, MutationUpsertTaskNoteArgs, TaskNote, PronounResponse } from '@/graphql/types';
import { Status, STATUSES } from '@/types/store';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { createAppAsyncThunk } from '../withTypes';
import { fetchPronouns, updatePronouns } from '@/api/pronouns';
import { RootState } from '..';
import { bookmarkTask } from '@/api/task';
import { TASK_STATUSES } from '@/types/assignments';
import { graphqlMutation, addRejectedToast, showRejectedToast } from '@/api/graphqlClient';
import { UPSERT_TASK_NOTE } from '@/graphql/queries';
import { errorMessages } from '@/utils/errorMessages';

// TODO:  get this from backend
export interface PronounsState {
  pronouns: PronounResponse[];
  status: Status;
  selectedTaskId: number | null;
  updatePronounStatus: Status;
  sendNoteStatus: Status;
}

const initialState: PronounsState = {
  pronouns: [],
  selectedTaskId: 0,
  status: STATUSES.IDLE,
  updatePronounStatus: STATUSES.IDLE,
  sendNoteStatus: STATUSES.IDLE,
};

/* THUNKS */
export const fetchPronounsThunk = createAppAsyncThunk(
  'assignment/fetchPronouns',
  async (payload: { assignmentId: number }) => {
    return await fetchPronouns(payload);
  },
);

export const updatePronounsThunk = createAppAsyncThunk(
  'assignment/updatePronouns',
  async (payload: CreateOrUpdatePronounTranslationInput) => {
    const translations = await updatePronouns(payload);
    return { tasks_pronouns_id: payload.tasks_pronouns_id, translations };
  },
);

export const bookmarkTaskThunk = createAppAsyncThunk(
  'assignment/bookmarkTask',
  async (payload: { taskId: number, isBookmarked: boolean }) => {
    const result = await bookmarkTask(payload);
    return { id: result.id, isBookmarked: payload.isBookmarked };
  },
);

export const sendNoteActionThunk = createAppAsyncThunk(
  'assignment/sendNote',
  async (payload: MutationUpsertTaskNoteArgs) => {
    const result = await graphqlMutation<MutationUpsertTaskNoteArgs, { upsertTaskNote: TaskNote }>(
      UPSERT_TASK_NOTE,
      payload,
    );
    const { upsertTaskNote } = result;
    return {
      upsertTaskNote,
      payload,
    };
  },
);

export const pronounReducer = createSlice({
  name: 'pronouns',
  initialState,
  reducers: {
    setSelectedTaskId: (state, action: PayloadAction<number | undefined>) => {
      state.selectedTaskId = action.payload || 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPronounsThunk.pending, (state) => {
      state.status = STATUSES.PENDING;
    });
    builder.addCase(fetchPronounsThunk.fulfilled, (state, action) => {
      state.pronouns = action.payload;
      state.status = STATUSES.FULFILLED;
      state.selectedTaskId = action.payload.find(task => task.status_id === TASK_STATUSES.ASSIGNED)?.task_pronouns_id ?? action.payload[0]?.task_pronouns_id ?? null;
    });
    builder.addCase(fetchPronounsThunk.rejected, (state, action) => {
      state.pronouns = initialState.pronouns;
      state.status = STATUSES.REJECTED;
      state.selectedTaskId = initialState.selectedTaskId;

      showRejectedToast(action, errorMessages.fetchingTasks);
    });

    // UPDATE
    builder.addCase(updatePronounsThunk.pending, (state) => {
      state.updatePronounStatus = STATUSES.PENDING;
    });
    builder.addCase(updatePronounsThunk.fulfilled, (state, action) => {
      const { tasks_pronouns_id, translations } = action.payload;
      const pronounIndex = state.pronouns.findIndex(p => p.task_pronouns_id === tasks_pronouns_id);
      if (state.pronouns[pronounIndex]) {
        state.pronouns[pronounIndex].status_id = TASK_STATUSES.COMPLETED;
        state.pronouns[pronounIndex].translations = translations;
      }
      state.updatePronounStatus = STATUSES.FULFILLED;
    });

    builder.addCase(updatePronounsThunk.rejected, (state, action) => {
      state.updatePronounStatus = STATUSES.REJECTED;
      showRejectedToast(action, errorMessages.updatingTask);
    });

    // Bookmark Task
    builder.addCase(bookmarkTaskThunk.fulfilled, (state, action) => {
      const { id: taskId, isBookmarked } = action.payload;
      const pronounIndex = state.pronouns.findIndex(p => p.task_id === taskId);
      if (state.pronouns[pronounIndex]) {
        state.pronouns[pronounIndex].is_bookmarked = isBookmarked;
      }
    });
    addRejectedToast(builder, bookmarkTaskThunk, errorMessages.bookmarkingTask);

    // NOTE
    builder.addCase(sendNoteActionThunk.pending, (state) => {
      state.sendNoteStatus = STATUSES.PENDING;
    });
    builder.addCase(sendNoteActionThunk.fulfilled, (state, action) => {
      const { upsertTaskNote, payload } = action.payload;
      const pronounIndex = state.pronouns.findIndex(p => p.task_pronouns_id === state.selectedTaskId);
      if (pronounIndex >= 0) {
        if (payload.noteId) {
          const notes = state.pronouns[pronounIndex].notes;
          const noteIndex = state.pronouns[pronounIndex].notes.findIndex(note => note.id === upsertTaskNote.id);
          if (notes && noteIndex >= 0) {
            notes[noteIndex] = upsertTaskNote;
          }
        } else {
          state.pronouns[pronounIndex].notes.push(upsertTaskNote);
        }
        state.sendNoteStatus = STATUSES.FULFILLED;
      } else {
        state.sendNoteStatus = STATUSES.REJECTED;
      }
    });
    addRejectedToast(builder, sendNoteActionThunk, errorMessages.savingNote);
  },
});

export const getPronouns = (state: RootState) => state.pronounsReducer.pronouns;

export const getAllPronouns = createSelector([getPronouns], (pronouns) => {
  return pronouns.map(task => ({
    task_id: task.task_id,
    id: Number(task.task_pronouns_id), // TODO: Connect-ui component should allow string
    isBookmarked: Boolean(Number(task.is_bookmarked)),
    status: task.status_id,
    title: task.source_text,
    content: '',
    translations: task.translations?.map(translation => (translation?.pronoun_text || '')) || [],
    notes: task.notes || [],
  }));
});

export const selectPronounTotalTasks = createSelector([getPronouns], tasks => tasks.length);
export const selectPronounCompletedTasks = createSelector([getPronouns], (tasks) => {
  return tasks.filter(({ status_id }) => status_id === TASK_STATUSES.COMPLETED).length;
});

export const {
  setSelectedTaskId,
} = pronounReducer.actions;
export default pronounReducer.reducer;
